export const BARCODE_TARGET_CLASS = 'barcode-target'

const INTER_KEY_RESET_MS = 100
const SCAN_KEY_GAP_MS = 50
const MIN_BARCODE_LENGTH = 3
const MAX_SCAN_DURATION_MS = 500
const TERMINATOR_KEYS = new Set(['Enter', 'Tab'])

const SCAN_DETECTED_STORAGE_KEY = 'agva_barcode_scanner_detected'

let initialized = false
let buffer = ''
let firstKeyTime = 0
let lastKeyTime = 0
let capturing = false
const scanListeners = new Set()

export function hasDetectedBarcodeScanner() {
  try {
    return sessionStorage.getItem(SCAN_DETECTED_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function markBarcodeScannerDetected() {
  try {
    sessionStorage.setItem(SCAN_DETECTED_STORAGE_KEY, '1')
  } catch {
    // Ignore storage failures (private mode, etc.).
  }
}

/**
 * Subscribe to barcode scan detections (keyboard-wedge scanners).
 * Returns an unsubscribe function.
 */
export function onBarcodeScanDetected(listener) {
  if (typeof listener !== 'function') {
    return () => {}
  }

  scanListeners.add(listener)
  return () => {
    scanListeners.delete(listener)
  }
}

function notifyBarcodeScanDetected(code) {
  markBarcodeScannerDetected()
  scanListeners.forEach((listener) => {
    try {
      listener(code)
    } catch {
      // Keep other listeners running if one throws.
    }
  })
}

function isVisible(el) {
  if (!el || el.disabled) {
    return false
  }

  const style = window.getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false
  }

  if (el.closest('.modal.show')) {
    return true
  }

  return el.offsetParent !== null || style.position === 'fixed'
}

function resolveInputElement(el) {
  if (!el) {
    return null
  }

  if (el.matches('input, textarea')) {
    return el
  }

  return el.querySelector('input:not([disabled]), textarea:not([disabled])')
}

function isHigherInStack(modal, than) {
  if (modal === than) {
    return false
  }

  const za = parseInt(window.getComputedStyle(modal).zIndex, 10) || 0
  const zb = parseInt(window.getComputedStyle(than).zIndex, 10) || 0

  if (za !== zb) {
    return za > zb
  }

  if (than.compareDocumentPosition(modal) & Node.DOCUMENT_POSITION_FOLLOWING) {
    return true
  }

  const stack = document.elementsFromPoint(window.innerWidth / 2, window.innerHeight / 2)

  for (const el of stack) {
    if (modal === el || modal.contains(el)) {
      return true
    }
    if (than === el || than.contains(el)) {
      return false
    }
  }

  return false
}

export function resolveBarcodeTarget() {
  const selector = `.${BARCODE_TARGET_CLASS}:not([disabled])`
  const targets = [...document.querySelectorAll(selector)]
    .map(resolveInputElement)
    .filter(el => el && isVisible(el))

  const modalTargets = targets
    .map(el => ({ el, modal: el.closest('.modal.show') }))
    .filter(({ modal }) => modal)

  if (modalTargets.length) {
    modalTargets.sort((a, b) => {
      if (isHigherInStack(a.modal, b.modal)) {
        return -1
      }
      if (isHigherInStack(b.modal, a.modal)) {
        return 1
      }
      return 0
    })

    const topModal = modalTargets[0].modal
    const inTopModal = modalTargets.filter(({ modal }) => modal === topModal)
    const active = document.activeElement
    const activeTarget = inTopModal.find(({ el }) => el === active)

    return activeTarget?.el || inTopModal[0].el
  }

  return targets.find(el => !el.closest('.modal'))
}

export function applyBarcode(el, code) {
  el.focus()
  el.dispatchEvent(new CustomEvent('barcode-scanned', { bubbles: true, detail: { code } }))
  el.value = code
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

function resetBuffer() {
  buffer = ''
  firstKeyTime = 0
  lastKeyTime = 0
  capturing = false
}

function isScanSequence() {
  if (buffer.length < MIN_BARCODE_LENGTH) {
    return false
  }

  const duration = lastKeyTime - firstKeyTime
  return duration <= MAX_SCAN_DURATION_MS
}

function handleKeyDown(event) {
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return
  }

  const now = Date.now()
  const isTerminator = TERMINATOR_KEYS.has(event.key)

  if (isTerminator) {
    if (capturing && isScanSequence()) {
      const code = buffer
      notifyBarcodeScanDetected(code)

      const target = resolveBarcodeTarget()
      if (target) {
        event.preventDefault()
        applyBarcode(target, code)
      }
    }

    resetBuffer()
    return
  }

  if (event.key.length !== 1) {
    return
  }

  if (lastKeyTime && now - lastKeyTime > INTER_KEY_RESET_MS) {
    resetBuffer()
  }

  if (!buffer) {
    firstKeyTime = now
  }

  const gap = lastKeyTime ? now - lastKeyTime : 0
  lastKeyTime = now
  buffer += event.key

  if (buffer.length >= 2 && gap < SCAN_KEY_GAP_MS) {
    capturing = true
  }

  if (capturing) {
    event.preventDefault()
  }
}

export function initBarcodeScanner() {
  if (initialized) {
    return
  }

  document.addEventListener('keydown', handleKeyDown, true)
  initialized = true
}
